import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, IconButton, Popover } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Tooltip from "@mui/material/Tooltip";

const TIME_SCALE_OPTIONS = [
  { label: "10m", value: "10M" },
  { label: "1h", value: "1H" },
  { label: "6h", value: "6H" },
  { label: "12h", value: "12H" },
  { label: "24h", value: "24H" },
];

const LeftToolbar = ({
  isPlaying,
  onTogglePlay,
  muted,
  onToggleMuted,
  timeScale,
  onSelectTimeScale,
}) => {
  const theme = useTheme();
  const [timeScaleAnchorEl, setTimeScaleAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setTimeScaleAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setTimeScaleAnchorEl(null);
  };

  const handleSelect = (scale) => {
    onSelectTimeScale(scale);
    handleClose();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#24336f" }}>
      <Tooltip title={isPlaying ? "Pause" : "Play"}>
        <IconButton
          size="small"
          onClick={onTogglePlay}
          sx={{ color: "#24336f", p: 0.35 }}
        >
          {isPlaying ? (
            <PauseCircleOutlineIcon sx={{ fontSize: 20 }} />
          ) : (
            <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title={muted ? "Unmute" : "Mute"}>
        <IconButton
          size="small"
          onClick={onToggleMuted}
          sx={{ color: "#24336f", p: 0.35 }}
        >
          {muted ? (
            <VolumeOffOutlinedIcon sx={{ fontSize: 19 }} />
          ) : (
            <VolumeUpOutlinedIcon sx={{ fontSize: 19 }} />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Snapshot">
        <IconButton size="small" sx={{ color: "#24336f", p: 0.35 }}>
          <CameraAltOutlinedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Zoom">
        <IconButton size="small" sx={{ color: "#24336f", p: 0.35 }}>
          <ZoomInOutlinedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Trim and download">
        <IconButton size="small" sx={{ color: "#24336f", p: 0.35 }}>
          <DownloadOutlinedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Time scale switch">
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpen}
          sx={{
            minWidth: 38,
            height: 26,
            px: 0.75,
            py: 0,
            border: "1.5px solid #24336f",
            borderColor: "#24336f",
            color: "#24336f",
            backgroundColor: "transparent",
            fontSize: 11,
            fontWeight: 700,
            borderRadius: "4px",
            textTransform: "none",
            "&:hover": {
              borderColor: "#24336f",
              backgroundColor: "rgba(36, 51, 111, 0.08)",
            },
          }}
        >
          {timeScale.toUpperCase()}
        </Button>
      </Tooltip>
      <Popover
        open={Boolean(timeScaleAnchorEl)}
        anchorEl={timeScaleAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              mb: 1,
              borderRadius: "6px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              border: "1px solid #e0e0e0",
              overflow: "hidden",
              minWidth: 58,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
          {TIME_SCALE_OPTIONS.map((opt) => (
            <Box
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              sx={{
                px: 1.5,
                py: 0.4,
                fontSize: 12,
                textAlign: "center",
                cursor: "pointer",
                fontWeight: timeScale === opt.value ? 600 : 400,
                color: timeScale === opt.value ? theme.palette.primary.main : "#333",
                backgroundColor:
                  timeScale === opt.value ? "rgba(23,38,100,0.08)" : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              {opt.label}
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

LeftToolbar.propTypes = {
  isPlaying: PropTypes.bool.isRequired,
  onTogglePlay: PropTypes.func.isRequired,
  muted: PropTypes.bool.isRequired,
  onToggleMuted: PropTypes.func.isRequired,
  timeScale: PropTypes.string.isRequired,
  onSelectTimeScale: PropTypes.func.isRequired,
};

export default LeftToolbar;
